import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../utils/axiosConfig";
import { Tooltip, Typography } from "@material-tailwind/react";
import { FaInfoCircle } from "react-icons/fa";
import Header from "../components/Header";
import {
  HearingStatus,
  PaymentStatus,
  HearingAgentStatus,
  HearingPaymentStatus,
} from "../utils/enums/hearing.enums";

interface HearingHistory {
  h_id: number;
  h_processNumber: string;
  hearingStatus: HearingStatus;
  h_hearingDate: string;
  hp_scheduledFor: string;
  fee: string;
  company: string;
  paymentStatus: keyof typeof PaymentStatus;
  hearingPaymentStatus?: HearingPaymentStatus | string;
  agentStatus?: number;
}

const HistoryPage: React.FC = () => {
  const [history, setHistory] = useState<HearingHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  console.log(history);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axiosInstance.get<HearingHistory[]>(
          "hearings/agent/history"
        );
        // Sort hearings by date and scheduled time
        const sortedHearings = response.data.sort((a, b) => {
          // Compare dates first
          const dateComparison =
            new Date(b.h_hearingDate).getTime() -
            new Date(a.h_hearingDate).getTime();
          if (dateComparison !== 0) return dateComparison;

          // If dates are equal, compare scheduled times (handle null/undefined values)
          const aScheduled = a.hp_scheduledFor || '';
          const bScheduled = b.hp_scheduledFor || '';
          return bScheduled.localeCompare(aScheduled);
        });
        // Filter out hearings with pending invitations (agentStatus = 1)
        const filteredHearings = sortedHearings.filter(
          (hearing) => hearing.agentStatus !== HearingAgentStatus.Convidado && hearing.agentStatus !== 1
        );
        setHistory(filteredHearings);
      } catch (error) {
        console.error("Error fetching history:", error);
        setError("Failed to load history.");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // Months are zero-based
    const year = date.getUTCFullYear();
    return `${day}/${month}/${year}`;
  };

  // Badge helpers (UI-only)
  const StatusBadge = ({ status }: { status: HearingStatus }) => {
    const map: Record<string, string> = {
      [HearingStatus.Agendada]: "bg-blue-50 text-blue-700 ring-1 ring-blue-200",
      [HearingStatus.Realizada]:
        "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
      [HearingStatus.Finalizada]:
        "bg-green-50 text-green-700 ring-1 ring-green-200",
      [HearingStatus.Cancelada]: "bg-red-50 text-red-700 ring-1 ring-red-200",
      [HearingStatus.Confirmada]: "bg-blue-50 text-blue-700 ring-1 ring-blue-200",
    } as any;
    const className = map[status] || "bg-gray-100 text-gray-700 ring-1 ring-gray-200";
    const label = status?.charAt(0).toUpperCase() + status?.slice(1);
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>
        {label}
      </span>
    );
  };

  // Situação financeira unificada (status do pagamento + data em uma célula só)
  const PaymentCell = ({ hearing }: { hearing: HearingHistory }) => {
    const badge = (label: string, className: string) => (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${className}`}
      >
        {label}
      </span>
    );
    const scheduledDate = hearing.hp_scheduledFor
      ? formatDate(hearing.hp_scheduledFor)
      : null;
    const ps = hearing.paymentStatus
      ? PaymentStatus[hearing.paymentStatus]
      : null;
    const isHearingPaymentCanceled =
      hearing.hearingPaymentStatus === HearingPaymentStatus.Cancelado;

    if (ps === PaymentStatus.REALIZADO) {
      return (
        <div className="flex flex-col items-start gap-0.5">
          {badge("Pago", "bg-green-50 text-green-700 ring-1 ring-green-200")}
          {scheduledDate && (
            <span className="text-xs text-slate-500 tabular-nums">{scheduledDate}</span>
          )}
        </div>
      );
    }
    if (ps === PaymentStatus.EM_PROCESSAMENTO || ps === PaymentStatus.RETENTATIVA) {
      return (
        <div className="flex flex-col items-start gap-0.5">
          {badge("Em processamento", "bg-amber-50 text-amber-700 ring-1 ring-amber-200")}
          {scheduledDate && (
            <span className="text-xs text-slate-500 tabular-nums">{scheduledDate}</span>
          )}
        </div>
      );
    }
    if (ps === PaymentStatus.NAO_REALIZADO) {
      return (
        <div className="flex flex-col items-start gap-0.5">
          {badge("Não realizado", "bg-red-50 text-red-700 ring-1 ring-red-200")}
          {scheduledDate && (
            <span className="text-xs text-slate-500 tabular-nums">{scheduledDate}</span>
          )}
        </div>
      );
    }
    if (ps === PaymentStatus.CANCELADO || isHearingPaymentCanceled) {
      return (
        <div className="flex flex-col items-start gap-0.5">
          {badge("Cancelado", "bg-red-50 text-red-700 ring-1 ring-red-200")}
          {scheduledDate && (
            <span className="text-xs text-slate-500 tabular-nums">{scheduledDate}</span>
          )}
        </div>
      );
    }
    if (scheduledDate) {
      return (
        <div className="flex flex-col items-start gap-0.5">
          {badge("Agendado", "bg-blue-50 text-blue-700 ring-1 ring-blue-200")}
          <span className="text-xs text-slate-500 tabular-nums">para {scheduledDate}</span>
        </div>
      );
    }
    if (hearing.hearingStatus === HearingStatus.Realizada) {
      return <span className="text-xs text-amber-700">Aguardando relatório</span>;
    }
    if (
      hearing.hearingStatus === HearingStatus.Agendada ||
      hearing.hearingStatus === HearingStatus.Confirmada
    ) {
      return <span className="text-xs text-slate-500">Aguardando audiência</span>;
    }
    return <span className="text-slate-400 text-sm">—</span>;
  };

  const totalScheduled = history
    .filter((hearing) => hearing.hearingStatus === HearingStatus.Agendada)
    .reduce((acc, hearing) => acc + (parseFloat(hearing.fee) || 0), 0);

  const totalPaid = history
    .filter(
      (hearing) =>
        PaymentStatus[hearing.paymentStatus] === PaymentStatus.REALIZADO
    )
    .reduce((acc, hearing) => acc + (parseFloat(hearing.fee) || 0), 0);

  const totalPending = history
    .filter(
      (hearing) =>
        PaymentStatus[hearing.paymentStatus] === PaymentStatus.EM_ABERTO ||
        PaymentStatus[hearing.paymentStatus] === PaymentStatus.RETENTATIVA ||
        PaymentStatus[hearing.paymentStatus] === PaymentStatus.NAO_REALIZADO
    )
    .reduce((acc, hearing) => acc + (parseFloat(hearing.fee) || 0), 0);

  // UI-only currency formatter for BRL values on summary cards
  const currency = useMemo(
    () =>
      new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 2,
      }),
    []
  );

  if (loading) {
    // Skeleton loader (UI-only)
    return (
      <>
        <Header />
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
          <div className="mb-4">
            <div className="h-6 w-64 bg-gray-200 rounded animate-pulse" />
            <div className="h-4 w-80 bg-gray-100 rounded mt-2 animate-pulse" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6 mb-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white border border-slate-100 rounded-xl p-5 shadow-sm">
                <div className="h-4 w-40 bg-gray-100 rounded animate-pulse" />
                <div className="h-7 w-24 bg-gray-200 rounded mt-4 animate-pulse" />
              </div>
            ))}
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <div className="h-10 bg-gray-50" />
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className={`h-12 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"} animate-pulse`} />
            ))}
          </div>
        </div>
      </>
    );
  }

  if (error) {
    return <div>{error}</div>;
  }

  const summaryItems = [
    {
      label: "A receber",
      value: totalScheduled,
      dotClass: "bg-blue-400",
      tooltip: "Valores de audiências realizadas recentemente ou agendadas.",
    },
    {
      label: "Em processamento",
      value: totalPending,
      dotClass: "bg-amber-400",
      tooltip:
        "Pagamentos processados, mas pendentes de confirmação. Em caso de erro, verifique sua chave PIX ou entre em contato com o suporte.",
    },
    {
      label: "Recebidos",
      value: totalPaid,
      dotClass: "bg-emerald-400",
      tooltip: "Pagamentos realizados com sucesso.",
    },
  ];

  // Agrupa por mês da audiência (history já vem ordenado do mais recente ao mais antigo)
  const monthGroups: Array<{
    key: string;
    label: string;
    items: HearingHistory[];
    total: number;
  }> = [];
  for (const hearing of history) {
    const date = new Date(hearing.h_hearingDate);
    const key = `${date.getUTCFullYear()}-${date.getUTCMonth()}`;
    let group = monthGroups.find((g) => g.key === key);
    if (!group) {
      const label = date.toLocaleDateString("pt-BR", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      });
      group = {
        key,
        label: label.charAt(0).toUpperCase() + label.slice(1),
        items: [],
        total: 0,
      };
      monthGroups.push(group);
    }
    group.items.push(hearing);
    group.total += parseFloat(hearing.fee) || 0;
  }

  return (
    <>
      <Header />
      <div className="container mx-auto p-3 md:p-4 lg:p-6">
        <div className="mb-6 bg-white border border-slate-100 rounded-xl shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
            {summaryItems.map((item) => (
              <div key={item.label} className="px-4 py-3 sm:py-4">
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden
                    className={`h-2 w-2 rounded-full shrink-0 ${item.dotClass}`}
                  />
                  <span className="text-xs md:text-sm font-medium text-slate-600">
                    {item.label}
                  </span>
                  <Tooltip
                    content={
                      <span className="block max-w-[240px] text-xs">{item.tooltip}</span>
                    }
                  >
                    <span className="cursor-help">
                      <FaInfoCircle className="text-slate-300 text-xs" />
                    </span>
                  </Tooltip>
                </div>
                <p className="mt-1.5 text-xl md:text-2xl font-bold tracking-tight text-preppoBlue">
                  {currency.format(item.value)}
                </p>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-2.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs md:text-[13px] text-slate-600">
            <span>
              Lotes de pagamento nos dias{" "}
              <span className="font-semibold text-slate-700">01</span>,{" "}
              <span className="font-semibold text-slate-700">10</span> e{" "}
              <span className="font-semibold text-slate-700">20</span> de cada
              mês
            </span>
            <Tooltip
              content={
                <span className="block max-w-[260px] text-xs">
                  Quando a data cai em fim de semana ou feriado, o pagamento é
                  realizado no próximo dia útil. Envie o relatório da audiência
                  antes da data do lote para que o pagamento entre nele.
                </span>
              }
            >
              <span className="cursor-help">
                <FaInfoCircle className="text-slate-300 text-xs" />
              </span>
            </Tooltip>
          </div>
        </div>

        <div className="flex items-end justify-between gap-3 mb-3">
          <div>
            <Typography variant="h4" className="text-preppoBlue tracking-tight !text-lg md:!text-xl">
              Audiências e pagamentos
            </Typography>
          </div>
        </div>

        {history.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[30vh] p-8 text-center border border-dashed border-gray-300 rounded-xl bg-white">
            <h3 className="text-lg md:text-xl font-semibold text-gray-900">Sem histórico por enquanto</h3>
            <p className="text-gray-500 mt-2 text-sm md:text-base">Quando houver audiências e pagamentos, eles aparecerão aqui.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  <th className="py-2.5 px-4 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">Audiência</th>
                  <th className="py-2.5 px-4 text-center font-semibold text-gray-700 uppercase tracking-wide text-xs">Data</th>
                  <th className="py-2.5 px-4 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">Pagamento</th>
                  <th className="py-2.5 px-4 text-right font-semibold text-gray-700 uppercase tracking-wide text-xs">Valor</th>
                </tr>
              </thead>
              <tbody className="text-preppoBlue">
                {monthGroups.map((group) => (
                  <React.Fragment key={group.key}>
                    <tr className="bg-slate-50/80">
                      <td colSpan={4} className="py-2 px-4 border-t border-slate-200">
                        <div className="flex items-center justify-between gap-3">
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            {group.label}
                          </span>
                          <span className="text-xs font-semibold text-slate-600 tabular-nums">
                            {currency.format(group.total)}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {group.items.map((hearing) => (
                      <tr
                        key={hearing.h_id}
                        className="bg-white hover:bg-blue-50/40 transition-colors"
                      >
                        <td className="py-2.5 px-4 border-t align-top max-w-[18rem]">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className="font-medium truncate"
                              title={hearing.h_processNumber}
                            >
                              {hearing.h_processNumber}
                            </span>
                            <StatusBadge status={hearing.hearingStatus} />
                          </div>
                          <p
                            className="text-xs text-slate-500 truncate mt-0.5"
                            title={hearing.company}
                          >
                            {hearing.company}
                          </p>
                        </td>
                        <td className="py-2.5 px-4 border-t text-center align-top text-sm sm:text-base tabular-nums whitespace-nowrap">
                          {formatDate(hearing.h_hearingDate)}
                        </td>
                        <td className="py-2.5 px-4 border-t align-top">
                          <PaymentCell hearing={hearing} />
                        </td>
                        <td className="py-2.5 px-4 border-t text-right align-top text-sm sm:text-base tabular-nums font-semibold whitespace-nowrap">
                          {currency.format(Number(hearing.fee || 0))}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default HistoryPage;
